import { Head, usePage } from '@inertiajs/react';
import { useMemo, useState } from 'react';

import { DocumentForm } from '@/components/admin/documents/DocumentForm';
import { DocumentsTable } from '@/components/admin/documents/DocumentsTable';
import type { Document } from '@/components/admin/documents/DocumentsTable';
import { Button } from '@/components/ui/Button';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';

interface DocumentsPageProps {
  documents: Document[];
  categories: string[];
  [key: string]: unknown;
}

export default function Documents() {
  const { documents, categories } = usePage<DocumentsPageProps>().props;
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);

  const categoryTabs = [
    { label: 'Todas', value: 'all' },
    ...categories.map((c) => ({ label: c, value: c })),
  ];

  const availableYears = useMemo(() => {
    const years = [...new Set(documents.map((d) => d.year))].sort((a, b) => b - a);
    return years;
  }, [documents]);

  const yearOptions = [
    { label: 'Todos os anos', value: 'all' },
    ...availableYears.map((y) => ({ label: String(y), value: String(y) })),
  ];

  const filtered = useMemo(() => {
    let result = documents;
    if (categoryFilter !== 'all') {
      result = result.filter((d) => d.category === categoryFilter);
    }
    if (yearFilter !== 'all') {
      result = result.filter((d) => d.year === Number(yearFilter));
    }
    return result;
  }, [documents, categoryFilter, yearFilter]);

  return (
    <>
      <Head title="Documentos — Administração" />
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-headline text-on-surface text-2xl font-bold">Documentos</h1>
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
            <span className="material-symbols-outlined text-[18px]">add</span>
            Novo documento
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <FilterTabs options={categoryTabs} value={categoryFilter} onChange={setCategoryFilter} />
          <Select options={yearOptions} value={yearFilter} onChange={setYearFilter} />
        </div>

        <div className="surface-card overflow-hidden">
          <DocumentsTable documents={filtered} />
        </div>
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Novo documento">
        <DocumentForm categories={categories} onClose={() => setShowForm(false)} />
      </Modal>
    </>
  );
}
